import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, History, RotateCcw, GitBranch, Link2Off, Copy, ArrowRightLeft } from "lucide-react";

const rawErr = (e, fb) => {
  const d = e.response?.data?._rawDetail ?? e.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fb;
};
const fmtAt = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

/* ───────────── Enrich / Edit ───────────── */
const ENRICH_TEXT = [
  ["product_name", "Product name"], ["brand", "Brand"], ["category", "Category"],
  ["warranty", "Warranty"], ["hsn_sac", "HSN / SAC"], ["gst_percent", "GST %"],
  ["standard_selling_price", "Selling price (₹)"], ["availability", "Availability"], ["lead_time", "Lead time"],
];
const NUM = new Set(["gst_percent", "standard_selling_price"]);

export function EnrichDialog({ id, meta, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/catalogue-workbench/products/${id}`).then(({ data: d }) => {
      setData(d);
      const p = d.product;
      const f = {};
      ENRICH_TEXT.forEach(([k]) => { f[k] = p[k] ?? ""; });
      f.description = p.description || "";
      f.specifications = typeof p.specifications === "string" ? p.specifications : "";
      f.selling_mode = p.selling_mode || "PRICE VISIBLE";
      f.tenant_visibility = p.tenant_visibility || "ALL";
      setForm(f);
    }).catch(() => toast.error("Could not load product"));
  }, [id]);
  const specsIsObject = data && data.product.specifications && typeof data.product.specifications !== "string";
  const save = async () => {
    const body = {};
    Object.entries(form).forEach(([k, v]) => {
      if (k === "specifications" && specsIsObject) return;
      if (v === "" || v == null) return;
      body[k] = NUM.has(k) ? Number(v) : v;
    });
    if (Object.values(body).some((v) => Number.isNaN(v))) return toast.error("GST and price must be numbers");
    setBusy(true);
    try {
      const { data: r } = await api.patch(`/catalogue-workbench/products/${id}`, body);
      toast.success(r.changed.length ? `Saved ${r.changed.length} field(s) · v${r.product.version}` : "No changes");
      onDone(); onClose();
    } catch (e) { toast.error(rawErr(e, "Save failed")); }
    setBusy(false);
  };
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="wb-enrich-dialog">
        <DialogHeader>
          <DialogTitle>Enrich product</DialogTitle>
          <DialogDescription>Fill the gaps that block publishing. Every save creates a restorable version.</DialogDescription>
        </DialogHeader>
        {!data ? <div className="py-8 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div> : (
          <div className="grid md:grid-cols-[1fr_220px] gap-4">
            <div className="space-y-2.5">
              <div className="grid grid-cols-2 gap-2.5">
                {ENRICH_TEXT.map(([k, label]) => (
                  <label key={k} className={`text-[11px] text-slate-500 ${k === "product_name" ? "col-span-2" : ""}`}>{label}
                    <Input className="h-8 mt-0.5 text-[13px]" value={form[k] ?? ""} onChange={set(k)} list={k === "brand" ? "wb-brands" : k === "category" ? "wb-cats" : undefined} data-testid={`wb-enrich-${k}`} />
                  </label>
                ))}
                <label className="text-[11px] text-slate-500">Selling mode
                  <Select value={form.selling_mode} onValueChange={(v) => setForm({ ...form, selling_mode: v })}>
                    <SelectTrigger className="h-8 mt-0.5 text-[13px]" data-testid="wb-enrich-selling_mode"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta.selling_modes || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </label>
                <label className="text-[11px] text-slate-500">Tenant visibility
                  <Select value={form.tenant_visibility} onValueChange={(v) => setForm({ ...form, tenant_visibility: v })}>
                    <SelectTrigger className="h-8 mt-0.5 text-[13px]" data-testid="wb-enrich-tenant_visibility"><SelectValue /></SelectTrigger>
                    <SelectContent>{["ALL", "SELECTED_TENANTS", "HIDDEN", "QUOTE_ONLY", "ORDERABLE"].map((v) => <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </label>
              </div>
              <label className="text-[11px] text-slate-500 block">Description
                <Textarea rows={3} className="mt-0.5 text-[13px]" value={form.description} onChange={set("description")} data-testid="wb-enrich-description" />
              </label>
              <label className="text-[11px] text-slate-500 block">Specifications {specsIsObject && <span className="text-slate-400">(structured — managed by Product Intelligence)</span>}
                <Textarea rows={4} className="mt-0.5 text-[13px] font-mono" disabled={!!specsIsObject}
                  value={specsIsObject ? JSON.stringify(data.product.specifications, null, 1).slice(0, 1500) : form.specifications}
                  onChange={set("specifications")} data-testid="wb-enrich-specifications" />
              </label>
            </div>
            <CompletenessPanel comp={data.completeness} blockers={data.publish_blockers} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-slate-900" onClick={save} disabled={busy || !data} data-testid="wb-enrich-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save version"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompletenessPanel({ comp, blockers }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="wb-completeness-panel">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">Completeness</div>
      <div className={`text-2xl font-semibold ${comp.score >= 100 ? "text-emerald-700" : comp.score >= 70 ? "text-amber-600" : "text-rose-600"}`} data-testid="wb-completeness-score">{comp.score}%</div>
      <div className="space-y-1.5">
        {Object.entries(comp.sections).map(([sec, pct]) => (
          <div key={sec}>
            <div className="flex justify-between text-[11px] text-slate-600"><span>{sec}</span><span>{pct}%</span></div>
            <div className="h-1.5 rounded bg-slate-200 overflow-hidden"><div className={`h-full ${pct >= 100 ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${pct}%` }} /></div>
          </div>
        ))}
      </div>
      {blockers?.length > 0 && (
        <div className="pt-1">
          <div className="text-[10px] uppercase tracking-wide text-rose-600">Blocking publish</div>
          <ul className="text-[11px] text-slate-700 space-y-0.5 mt-1" data-testid="wb-completeness-missing">{blockers.map((b) => <li key={b}>• {b}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

/* ───────────── Version history ───────────── */
export function HistoryDialog({ row, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get(`/catalogue-workbench/products/${row.id}/versions`).then(({ data: d }) => setData(d)).catch(() => setData({ items: [] })), [row.id]);
  useEffect(() => { load(); }, [load, row.id]);
  const restore = async (v) => {
    if (!window.confirm(`Restore ${row.product_name} to version ${v}? The current state is saved first.`)) return;
    setBusy(v);
    try {
      await api.post(`/catalogue-workbench/products/${row.id}/restore`, { version: v });
      toast.success(`Restored to v${v}`); onDone(); load();
    } catch (e) { toast.error(rawErr(e, "Restore failed")); }
    setBusy(null);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="wb-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-4 w-4" /> Version history · {row.product_name}</DialogTitle>
          <DialogDescription>Each edit, publish, bulk change or propagation saves the record as it was <em>before</em> the change. Current version: v{data?.current_version ?? "…"}.</DialogDescription>
        </DialogHeader>
        {!data ? <div className="py-8 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
          : data.items.length === 0 ? <div className="py-8 text-center text-slate-400" data-testid="wb-history-empty">No versions yet — the first edit will create one.</div> : (
            <div className="divide-y divide-slate-100 border border-slate-200 rounded-md">
              {data.items.map((v) => (
                <div key={v.version} className="p-3 flex gap-3 items-start" data-testid={`wb-version-${v.version}`}>
                  <div className="w-12 shrink-0 text-center"><div className="text-sm font-semibold text-slate-900">v{v.version}</div><div className="text-[10px] text-slate-400">{v.completeness}%</div></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] text-slate-800"><span className="font-medium capitalize">{v.reason}</span> · {fmtAt(v.at)} · {v.by?.split("@")[0]}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-1">
                      <Badge className="text-[10px] bg-slate-100 text-slate-700 border-slate-200">{v.catalogue_status}</Badge>
                      {v.published && <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200">was published</Badge>}
                      {v.fields_changed_after.length > 0 && <span className="text-slate-500">then changed: {v.fields_changed_after.slice(0, 8).join(", ")}{v.fields_changed_after.length > 8 ? "…" : ""}</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">{v.product_name} · warranty {v.warranty || "—"} · HSN {v.hsn_sac || "—"} · GST {v.gst_percent ?? "—"} · ₹{v.standard_selling_price ?? "—"}</div>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => restore(v.version)} disabled={busy != null} data-testid={`wb-restore-${v.version}`}>
                    {busy === v.version ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RotateCcw className="h-3 w-3 mr-1" />Restore</>}
                  </Button>
                </div>
              ))}
            </div>
          )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────── Families / variants ───────────── */
const parseAttrs = (txt) => Object.fromEntries((txt || "").split(",").map((s) => s.split("=").map((x) => x.trim())).filter(([k, v]) => k && v));
const attrText = (a) => Object.entries(a || {}).map(([k, v]) => `${k}: ${v}`).join(" · ");

export function FamilyDialog({ row, onClose, onDone }) {
  const [fam, setFam] = useState(null);
  const [famId, setFamId] = useState(row.family_id);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(row.family_name || row.product_name);
  const load = (fid) => api.get(`/catalogue-workbench/families/${fid}`).then(({ data }) => { setFam(data); setName(data.family_name || ""); }).catch(() => toast.error("Could not load family"));
  useEffect(() => { if (famId) load(famId); }, [famId]);
  const createFamily = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/catalogue-workbench/families", { family_name: name, product_ids: [row.id] });
      setFamId(data.family_id); onDone(); toast.success("Family created — add variants below");
    } catch (e) { toast.error(rawErr(e, "Could not create family")); }
    setBusy(false);
  };
  const rename = async () => {
    try { await api.patch(`/catalogue-workbench/families/${famId}`, { family_name: name }); toast.success("Renamed"); load(famId); onDone(); } catch (e) { toast.error(rawErr(e, "Rename failed")); }
  };
  const unlink = async (id) => {
    if (!window.confirm("Remove this product from the family?")) return;
    try { await api.post(`/catalogue-workbench/products/${id}/family`, { family_id: null }); toast.success("Unlinked"); onDone(); if (fam.members.length <= 1) onClose(); else load(famId); } catch (e) { toast.error(rawErr(e, "Unlink failed")); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="wb-family-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitBranch className="h-4 w-4" /> Product family &amp; variants</DialogTitle>
          <DialogDescription>Group colour / storage / memory variants under one family. Shared data can be propagated so variants never drift.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 items-center">
          <Input className="h-8 text-[13px]" value={name} onChange={(e) => setName(e.target.value)} placeholder="Family name" data-testid="wb-family-name" />
          {famId ? <Button size="sm" variant="outline" className="h-8" onClick={rename} data-testid="wb-family-rename">Rename</Button>
            : <Button size="sm" className="h-8 bg-slate-900" onClick={createFamily} disabled={busy} data-testid="wb-family-create">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Create family"}</Button>}
        </div>
        {famId && !fam && <div className="py-6 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>}
        {fam && (
          <>
            {fam.inconsistent_fields.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900" data-testid="wb-family-inconsistent">
                Variants differ on: <span className="font-medium">{fam.inconsistent_fields.join(", ")}</span>. Use “Propagate” from the correct variant to align them.
              </div>
            )}
            <div className="border border-slate-200 rounded-md overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500"><tr>
                  <th className="px-2 py-1.5 text-left">Variant</th><th className="px-2 py-1.5 text-left">Part No.</th>
                  <th className="px-2 py-1.5 text-left">Attributes</th><th className="px-2 py-1.5 text-left">Status</th><th className="px-2 py-1.5 text-right">Actions</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {fam.members.map((m) => <MemberRow key={m.id} m={m} fam={fam} self={m.id === row.id} onChanged={() => { load(famId); onDone(); }} onUnlink={() => unlink(m.id)} />)}
                </tbody>
              </table>
            </div>
            <AddVariantForm source={row} onAdded={() => { load(famId); onDone(); }} />
          </>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MemberRow({ m, fam, self, onChanged, onUnlink }) {
  const [mode, setMode] = useState(null); // attrs | propagate
  const [attrs, setAttrs] = useState(Object.entries(m.variant_attributes || {}).map(([k, v]) => `${k}=${v}`).join(", "));
  const [fields, setFields] = useState(["specifications", "description", "warranty", "hsn_sac", "gst_percent", "primary_image", "documents"]);
  const [busy, setBusy] = useState(false);
  const saveAttrs = async () => {
    setBusy(true);
    try { await api.post(`/catalogue-workbench/products/${m.id}/family`, { variant_attributes: parseAttrs(attrs) }); toast.success("Attributes saved"); setMode(null); onChanged(); } catch (e) { toast.error(rawErr(e, "Save failed")); }
    setBusy(false);
  };
  const propagate = async () => {
    if (!fields.length) return toast.error("Pick at least one field");
    if (!window.confirm(`Copy ${fields.length} field(s) from "${m.product_name}" to the other ${fam.members.length - 1} variant(s)?`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/catalogue-workbench/families/${fam.family_id}/propagate`, { from_id: m.id, fields }); toast.success(`Propagated to ${data.affected} variant(s)`); setMode(null); onChanged(); } catch (e) { toast.error(rawErr(e, "Propagate failed")); }
    setBusy(false);
  };
  return (
    <>
      <tr className={self ? "bg-sky-50/60" : ""} data-testid={`wb-family-member-${m.id}`}>
        <td className="px-2 py-1.5"><div className="font-medium text-slate-900 truncate max-w-[260px]">{m.product_name}</div>{self && <span className="text-[10px] text-sky-700">this product</span>}</td>
        <td className="px-2 py-1.5 font-mono text-[11px]">{m.part_number || "—"}</td>
        <td className="px-2 py-1.5 text-slate-600">{attrText(m.variant_attributes) || <span className="text-slate-400">no attributes</span>}</td>
        <td className="px-2 py-1.5"><Badge className="text-[10px] bg-slate-100 text-slate-700 border-slate-200">{m.catalogue_status}</Badge> <span className="text-[10px] text-slate-400">{m.completeness}%</span></td>
        <td className="px-2 py-1.5 text-right whitespace-nowrap">
          <button className="text-[11px] underline text-slate-700 mr-2" onClick={() => setMode(mode === "attrs" ? null : "attrs")} data-testid={`wb-member-attrs-${m.id}`}>Attributes</button>
          {fam.members.length > 1 && <button className="text-[11px] underline text-indigo-700 mr-2" onClick={() => setMode(mode === "propagate" ? null : "propagate")} data-testid={`wb-member-propagate-${m.id}`}><ArrowRightLeft className="h-3 w-3 inline mr-0.5" />Propagate</button>}
          <button className="text-[11px] underline text-rose-600" onClick={onUnlink} data-testid={`wb-member-unlink-${m.id}`}><Link2Off className="h-3 w-3 inline mr-0.5" />Unlink</button>
        </td>
      </tr>
      {mode === "attrs" && (
        <tr><td colSpan={5} className="px-2 py-2 bg-slate-50">
          <div className="flex gap-2 items-center">
            <Input className="h-8 text-[12px]" placeholder="Colour=Space Grey, Storage=512GB, Memory=16GB" value={attrs} onChange={(e) => setAttrs(e.target.value)} data-testid={`wb-member-attrs-input-${m.id}`} />
            <Button size="sm" className="h-8 bg-slate-900" onClick={saveAttrs} disabled={busy} data-testid={`wb-member-attrs-save-${m.id}`}>Save</Button>
          </div>
        </td></tr>
      )}
      {mode === "propagate" && (
        <tr><td colSpan={5} className="px-2 py-2 bg-indigo-50/60">
          <div className="text-[11px] text-slate-600 mb-1.5">Fields to copy from this variant to all others:</div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {fam.shared_fields.map((f) => (
              <button key={f} onClick={() => setFields((s) => s.includes(f) ? s.filter((x) => x !== f) : [...s, f])}
                className={`px-2 py-0.5 rounded-full border text-[11px] ${fields.includes(f) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-300"} ${fam.inconsistent_fields.includes(f) ? "ring-1 ring-amber-400" : ""}`}
                data-testid={`wb-propagate-field-${f}`}>{f.replace(/_/g, " ")}</button>
            ))}
          </div>
          <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700" onClick={propagate} disabled={busy} data-testid={`wb-propagate-confirm-${m.id}`}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : `Propagate ${fields.length} field(s)`}</Button>
        </td></tr>
      )}
    </>
  );
}

function AddVariantForm({ source, onAdded }) {
  const [form, setForm] = useState({ sku: "", attrs: "", product_name: "" });
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const submit = async (force = false) => {
    if (!form.sku.trim()) return toast.error("Variant Part Number / SKU is required");
    setBusy(true); setDup(null);
    try {
      await api.post(`/catalogue-workbench/products/${source.id}/copy`, {
        sku: form.sku.trim(), product_name: form.product_name.trim() || undefined,
        variant_attributes: parseAttrs(form.attrs), force_variant: force });
      toast.success("Variant added as Draft"); setForm({ sku: "", attrs: "", product_name: "" }); onAdded();
    } catch (e) {
      const d = e.response?.data?._rawDetail ?? e.response?.data?.detail;
      if (d?.code === "DUPLICATE_PART_NUMBER") { setDup(d.existing); toast.error("Part number already exists"); } else toast.error(rawErr(e, "Could not add variant"));
    }
    setBusy(false);
  };
  return (
    <div className="rounded-md border border-dashed border-slate-300 p-3 space-y-2" data-testid="wb-add-variant">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1"><Copy className="h-3 w-3" /> Add variant (copies {source.product_name})</div>
      <div className="grid sm:grid-cols-3 gap-2">
        <Input className="h-8 text-[12px]" placeholder="Part Number / SKU *" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="wb-variant-sku" />
        <Input className="h-8 text-[12px]" placeholder="Colour=Silver, Storage=1TB" value={form.attrs} onChange={(e) => setForm({ ...form, attrs: e.target.value })} data-testid="wb-variant-attrs" />
        <Input className="h-8 text-[12px]" placeholder="Name (optional — auto from attributes)" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} data-testid="wb-variant-name" />
      </div>
      {dup && <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="wb-variant-dup">Exists: {dup.product_name} ({dup.sku || dup.oem_part_number}). <button className="underline" onClick={() => submit(true)}>Create anyway</button></div>}
      <Button size="sm" className="h-8 bg-slate-900" onClick={() => submit(false)} disabled={busy} data-testid="wb-variant-add">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Add variant"}</Button>
    </div>
  );
}

/* ───────────── Bulk: set fields / group family ───────────── */
const BULK_INPUTS = [["warranty", "Warranty"], ["hsn_sac", "HSN / SAC"], ["gst_percent", "GST %"], ["availability", "Availability"], ["lead_time", "Lead time"], ["category", "Category"], ["brand", "Brand"]];

export function BulkFieldsDialog({ ids, onClose, onDone }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const fields = {};
    Object.entries(form).forEach(([k, v]) => { if (v !== "" && v != null) fields[k] = k === "gst_percent" ? Number(v) : v; });
    if (!Object.keys(fields).length) return toast.error("Fill at least one field");
    if (Number.isNaN(fields.gst_percent)) return toast.error("GST must be a number");
    setBusy(true);
    try {
      const { data } = await api.post("/catalogue-workbench/bulk", { action: "set_fields", ids, payload: { fields } });
      toast.success(`Updated ${data.affected} product(s) — versions saved`); onDone(); onClose();
    } catch (e) { toast.error(rawErr(e, "Bulk edit failed")); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="wb-bulk-fields-dialog">
        <DialogHeader><DialogTitle>Set fields on {ids.length} product(s)</DialogTitle><DialogDescription>Only filled fields are written. Each product is versioned before the change.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-2.5">
          {BULK_INPUTS.map(([k, label]) => (
            <label key={k} className="text-[11px] text-slate-500">{label}
              <Input className="h-8 mt-0.5 text-[13px]" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`wb-bulk-field-${k}`} />
            </label>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-slate-900" onClick={submit} disabled={busy} data-testid="wb-bulk-fields-confirm">Apply to {ids.length}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function GroupFamilyDialog({ ids, onClose, onDone }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (merge = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/catalogue-workbench/families", { family_name: name, product_ids: ids, merge });
      toast.success(`Grouped ${data.count} product(s) into “${data.family_name}”`); onDone(); onClose();
    } catch (e) {
      const d = e.response?.data?._rawDetail ?? e.response?.data?.detail;
      if (d?.code === "FAMILY_CONFLICT" && window.confirm("Selected products already belong to different families. Merge them into one?")) { setBusy(false); return submit(true); }
      toast.error(rawErr(e, "Grouping failed"));
    }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="wb-group-family-dialog">
        <DialogHeader><DialogTitle>Group {ids.length} product(s) as a family</DialogTitle><DialogDescription>Variants of the same model (colour, storage, memory) share one family so data can be propagated.</DialogDescription></DialogHeader>
        <Input placeholder="Family name (defaults to first product name)" value={name} onChange={(e) => setName(e.target.value)} data-testid="wb-group-family-name" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-slate-900" onClick={() => submit(false)} disabled={busy} data-testid="wb-group-family-confirm">Group</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
